import { useState } from "react";
import { Link } from "react-router-dom";
import { DEV_BYPASS_ENABLED } from "@/lib/auth-context";
import { Sparkles, Shield, LogIn, LayoutDashboard, X, ChevronUp } from "lucide-react";

/**
 * Floating dev shortcuts pill. Only renders when REACT_APP_ENABLE_DEV_BYPASS=true.
 * Provides one-click access to admin login, clinic login, and portal — for dev use only.
 */
export default function DevQuickAccess() {
  const [open, setOpen] = useState(false);
  if (!DEV_BYPASS_ENABLED) return null;

  return (
    <div
      data-testid="dev-quick-access"
      className="fixed bottom-4 left-4 z-[60] print:hidden"
    >
      {open ? (
        <div className="rounded-2xl border border-amber-200 bg-white shadow-xl w-64 overflow-hidden">
          <div className="flex items-center justify-between px-4 py-2.5 bg-amber-50 border-b border-amber-100">
            <div className="flex items-center gap-1.5 text-[11px] uppercase tracking-[0.18em] font-bold text-amber-700">
              <Sparkles className="w-3.5 h-3.5" /> Dev shortcuts
            </div>
            <button
              type="button"
              onClick={() => setOpen(false)}
              data-testid="dev-quick-access-close"
              className="text-amber-700 hover:text-amber-900 w-6 h-6 inline-flex items-center justify-center rounded-md"
              aria-label="Close"
            >
              <X className="w-3.5 h-3.5" />
            </button>
          </div>
          <div className="p-2 space-y-1">
            <DevLink to="/admin" icon={Shield} label="Admin login" sub="/admin" testId="dev-link-admin" />
            <DevLink to="/admin/dashboard" icon={LayoutDashboard} label="Admin dashboard" sub="(if logged in)" testId="dev-link-admin-dashboard" />
            <DevLink to="/login" icon={LogIn} label="Clinic login" sub="/login" testId="dev-link-login" />
            <DevLink to="/portal" icon={LayoutDashboard} label="Clinic portal" sub="(if logged in)" testId="dev-link-portal" />
          </div>
          <div className="px-3 py-2 border-t border-slate-100 text-[10.5px] text-slate-500 leading-snug">
            Hidden in production via REACT_APP_ENABLE_DEV_BYPASS=false.
          </div>
        </div>
      ) : (
        <button
          type="button"
          onClick={() => setOpen(true)}
          data-testid="dev-quick-access-toggle"
          className="inline-flex items-center gap-2 bg-amber-50 text-amber-900 border border-amber-200 rounded-full pl-3 pr-4 py-2 text-xs font-semibold shadow-md hover:bg-amber-100"
        >
          <Sparkles className="w-3.5 h-3.5" />
          Dev
          <ChevronUp className="w-3 h-3" />
        </button>
      )}
    </div>
  );
}

function DevLink({ to, icon: Icon, label, sub, testId }) {
  return (
    <Link
      to={to}
      data-testid={testId}
      className="flex items-center gap-2.5 rounded-lg px-2.5 py-2 hover:bg-slate-50"
    >
      <span className="w-7 h-7 rounded-md bg-slate-900 text-white flex items-center justify-center shrink-0">
        <Icon className="w-3.5 h-3.5" />
      </span>
      <div className="flex-1 min-w-0">
        <div className="text-sm font-semibold text-slate-900 leading-tight">{label}</div>
        <div className="text-[10.5px] text-slate-500 truncate">{sub}</div>
      </div>
    </Link>
  );
}
